import React, { useCallback, useContext, useMemo } from 'react';

import { ChannelEntity } from '../components/ChannelEntity';
import { EmojiEntity } from '../components/EmojiEntity';
import { ProfileEntity } from '../components/ProfileEntity';
import { SpammerEntity } from '../components/SpammerEntity';
import { EntitySnapshotContext } from '../contexts/EntitySnapshotContext';

const EMPTY_ENTITY_MAP = Object.freeze({});

/**
 * Hook that provides entity replacement logic for both html-react-parser and Streamdown
 * Returns a function that can replace entity DOM nodes with React components
 */
export function useEntityReplacer(entitySnapshots = {}, options = {}) {
  const { emoji: globalEmojiById, emojiByShortcode: globalEmojiByShortcode } = useContext(EntitySnapshotContext) || {};
  const { containsOnlyEntities = false, containsOnlyEmojiEntities = false, popoverProfileEntities = false, truncated = false } = options;

  const channelEntities = entitySnapshots?.channel || EMPTY_ENTITY_MAP;
  const profileEntities = entitySnapshots?.profile || EMPTY_ENTITY_MAP;
  const emojiEntities = entitySnapshots?.emoji || null;

  const emojiById = useMemo(() => {
    if (!emojiEntities) return null;
    return new Map(Object.entries(emojiEntities));
  }, [emojiEntities]);

  const emojiByShortcode = useMemo(() => {
    if (!emojiEntities) return null;
    const map = new Map();
    Object.values(emojiEntities).forEach((snapshot) => {
      if (!snapshot?.shortcode || map.has(snapshot.shortcode)) return;
      map.set(snapshot.shortcode, snapshot);
    });
    return map;
  }, [emojiEntities]);

  const resolveEmoji = useCallback(
    (id, shortcode) => {
      if (id) {
        const byId = emojiById?.get(id) ?? globalEmojiById?.get?.(id);
        if (byId) return byId;
      }
      if (shortcode) {
        const byShortcode = emojiByShortcode?.get(shortcode) ?? globalEmojiByShortcode?.get?.(shortcode);
        if (byShortcode) return byShortcode;
      }
      return null;
    },
    [emojiById, emojiByShortcode, globalEmojiById, globalEmojiByShortcode],
  );

  // function for html-react-parser's replace option
  const replaceEntityNode = useCallback(
    (domNode) => {
      if (domNode.name === 'entity' && domNode.attribs) {
        const { 'data-type': dataType, 'data-target': dataTarget, 'data-id': dataId, 'data-name': dataName } = domNode.attribs;

        if (dataType === 'channel' && dataTarget) {
          const channel = channelEntities?.[dataTarget] ?? null;
          return <ChannelEntity channel={channel} jumbo={containsOnlyEntities} truncated={truncated} dataTarget={dataTarget} />;
        } else if (dataType === 'profile' && dataTarget) {
          const profile = profileEntities?.[dataTarget] ?? null;
          return (
            <ProfileEntity
              profile={profile}
              jumbo={containsOnlyEntities}
              truncated={truncated}
              dataTarget={dataTarget}
              dataId={dataId}
              popoverOnClick={popoverProfileEntities}
            />
          );
        } else if (dataType === 'spammer' && dataTarget) {
          const spammer = { id: dataTarget };
          return <SpammerEntity spammer={spammer} jumbo={containsOnlyEntities} truncated={truncated} dataTarget={dataTarget} />;
        } else if (dataType === 'emoji') {
          let emoji = null;
          if (dataId) emoji = resolveEmoji(dataId, dataName);
          if (!emoji && dataName) emoji = resolveEmoji(null, dataName);
          return (
            <EmojiEntity
              emoji={emoji}
              jumbo={containsOnlyEntities}
              superJumbo={containsOnlyEmojiEntities}
              dataId={dataId}
              dataName={dataName}
            />
          );
        }
      }
      return undefined;
    },
    [channelEntities, truncated, containsOnlyEntities, containsOnlyEmojiEntities, resolveEmoji, profileEntities, popoverProfileEntities],
  );

  // function for replacing DOM elements (for Streamdown)
  const createEntityComponent = useCallback(
    (element) => {
      const dataType = element.getAttribute('data-type');
      const dataTarget = element.getAttribute('data-target');
      const dataId = element.getAttribute('data-id');
      const dataName = element.getAttribute('data-name');

      if (dataType === 'channel' && dataTarget) {
        const channel = channelEntities?.[dataTarget] ?? null;
        return <ChannelEntity channel={channel} jumbo={containsOnlyEntities} truncated={truncated} dataTarget={dataTarget} />;
      } else if (dataType === 'profile' && dataTarget) {
        const profile = profileEntities?.[dataTarget] ?? null;
        return (
          <ProfileEntity
            profile={profile}
            jumbo={containsOnlyEntities}
            truncated={truncated}
            dataTarget={dataTarget}
            dataId={dataId}
            popoverOnClick={popoverProfileEntities}
          />
        );
      } else if (dataType === 'spammer' && dataTarget) {
        const spammer = { id: dataTarget };
        return <SpammerEntity spammer={spammer} jumbo={containsOnlyEntities} truncated={truncated} dataTarget={dataTarget} />;
      } else if (dataType === 'emoji') {
        let emoji = null;
        if (dataId) emoji = resolveEmoji(dataId, dataName);
        if (!emoji && dataName) emoji = resolveEmoji(null, dataName);
        return (
          <EmojiEntity
            emoji={emoji}
            jumbo={containsOnlyEntities}
            superJumbo={containsOnlyEmojiEntities}
            dataId={dataId}
            dataName={dataName}
          />
        );
      }
      return null;
    },
    [channelEntities, truncated, containsOnlyEntities, containsOnlyEmojiEntities, resolveEmoji, profileEntities, popoverProfileEntities],
  );

  return { replaceEntityNode, createEntityComponent };
}
